<template>
  <transition name="security-fade">
    <div v-if="visible" class="security-overlay" :class="{ 'dark-theme': isDark }">
      <div class="security-card">
        <div class="security-icon">
          <div class="icon-ring">
            <i class="fa-solid fa-shield-halved"></i>
          </div>
          <div class="pulse-ring"></div>
        </div>
        <h1 class="security-title">访问受限</h1>
        <p class="security-desc">当前您的网络环境不安全，请更换网络环境后再尝试</p>
        <div class="security-status">
          <span class="status-dot"></span>
          <span>正在检测网络环境状态...</span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import serverUrl from '@/assets/BaseServerUrl.js'

export default {
  name: 'SecurityBlockOverlay',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDark: false,
      observer: null,
      checkTimer: null
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.startCheckLoop()
      } else {
        this.stopCheckLoop()
      }
    }
  },
  mounted() {
    this.checkTheme()
    if (typeof MutationObserver !== 'undefined') {
      this.observer = new MutationObserver(() => this.checkTheme())
      this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
      this.observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    }
    if (this.visible) {
      this.startCheckLoop()
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
    this.stopCheckLoop()
  },
  methods: {
    checkTheme() {
      const bodyHas = typeof document !== 'undefined' && document.body && document.body.classList.contains('dark-theme')
      const htmlHas = typeof document !== 'undefined' && document.documentElement && document.documentElement.classList.contains('dark-theme')
      this.isDark = !!(bodyHas || htmlHas)
    },
    startCheckLoop() {
      this.stopCheckLoop()
      // 每10秒检测一次是否已解封
      this.checkTimer = setInterval(async () => {
        try {
          const resp = await fetch(`${serverUrl}/api/health`, { method: 'GET' })
          if (resp.ok) {
            // 请求成功，说明IP已解封，刷新页面
            window.location.reload()
          }
        } catch (e) {
          // 仍然无法访问，继续等待
        }
      }, 10000)
    },
    stopCheckLoop() {
      if (this.checkTimer) {
        clearInterval(this.checkTimer)
        this.checkTimer = null
      }
    }
  }
}
</script>

<style scoped>
.security-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-critical);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(245, 245, 247, 0.92);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
}

.security-card {
  text-align: center;
  padding: 60px 48px;
  max-width: 460px;
  width: 90%;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08), 0 0 0 0.5px rgba(0, 0, 0, 0.04);
}

.security-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
}

.icon-ring {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fff4e6, #ffe0b3);
  border: 1px solid rgba(255, 149, 0, 0.15);
  font-size: 32px;
  color: #ff9500;
  position: relative;
  z-index: 1;
}

.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px solid rgba(255, 149, 0, 0.3);
  animation: pulse 2s ease-out infinite;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.6;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.8);
    opacity: 0;
  }
}

.security-title {
  font-size: 28px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;
}

.security-desc {
  font-size: 16px;
  color: #86868b;
  line-height: 1.6;
  margin: 0 0 28px 0;
}

.security-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #86868b;
  padding: 8px 18px;
  border-radius: 100px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff9500;
  animation: blink 1.5s ease-in-out infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.security-fade-enter-active,
.security-fade-leave-active {
  transition: opacity 0.5s ease;
}

.security-fade-enter-from,
.security-fade-leave-to {
  opacity: 0;
}

/* 深色模式完整兼容样式 */
:global(body.dark-theme) .security-overlay,
:global(html.dark-theme) .security-overlay,
.security-overlay.dark-theme {
  background: rgba(28, 28, 30, 0.94);
}

:global(body.dark-theme) .security-card,
:global(html.dark-theme) .security-card,
.security-overlay.dark-theme .security-card {
  background: rgba(44, 44, 46, 0.85);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4), 0 0 0 0.5px rgba(255, 255, 255, 0.06);
}

:global(body.dark-theme) .icon-ring,
:global(html.dark-theme) .icon-ring,
.security-overlay.dark-theme .icon-ring {
  background: linear-gradient(135deg, #3a3a3c, #2c2c2e);
  border-color: rgba(255, 149, 0, 0.2);
  color: #ffa726;
}

:global(body.dark-theme) .pulse-ring,
:global(html.dark-theme) .pulse-ring,
.security-overlay.dark-theme .pulse-ring {
  border-color: rgba(255, 149, 0, 0.2);
}

:global(body.dark-theme) .security-title,
:global(html.dark-theme) .security-title,
.security-overlay.dark-theme .security-title {
  color: #f5f5f7;
}

:global(body.dark-theme) .security-desc,
:global(html.dark-theme) .security-desc,
.security-overlay.dark-theme .security-desc {
  color: #98989d;
}

:global(body.dark-theme) .security-status,
:global(html.dark-theme) .security-status,
.security-overlay.dark-theme .security-status {
  color: #98989d;
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.06);
}
</style>
